import React, { useEffect, useState } from 'react';
import { PageLayout } from '../../shared/PageLayout';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { Select } from '../../../components/ui/Select';
import { Toggle } from '../../../components/ui/Toggle';
import { Button } from '../../../components/ui/Button';
import { Settings, Shield, Bell, Database, Info, Check } from 'lucide-react';
import { dataLayer } from '../../../data/dataLayer';
interface SettingToggleProps {
  label: string;
  description: string;
  value: boolean;
  onChange: (v: boolean) => void;
}
function SettingToggle({
  label,
  description,
  value,
  onChange
}: SettingToggleProps) {
  return <div className="flex items-center justify-between py-4 border-b border-gray-50 last:border-0" data-id="element-4225">
      <div className="flex-1 pr-4" data-id="element-4226">
        <div className="text-sm font-medium text-gray-900" data-id="element-4227">{label}</div>
        <div className="text-xs text-gray-500 mt-0.5" data-id="element-4228">{description}</div>
      </div>
      <Toggle checked={value} onChange={onChange} label={label} variant="admin" data-id="element-4229" />
    </div>;
}
interface SettingSelectProps {
  label: string;
  description: string;
  value: string;
  options: Array<{
    value: string;
    label: string;
  }>;
  onChange: (v: string) => void;
}
function SettingSelect({
  label,
  description,
  value,
  options,
  onChange
}: SettingSelectProps) {
  return <div className="flex flex-col sm:flex-row sm:items-center justify-between py-4 border-b border-gray-50 last:border-0 gap-3" data-id="element-4230">
      <div className="flex-1 pr-4" data-id="element-4231">
        <div className="text-sm font-medium text-gray-900" data-id="element-4232">{label}</div>
        <div className="text-xs text-gray-500 mt-0.5" data-id="element-4233">{description}</div>
      </div>
      <Select options={options} value={value} onChange={e => onChange(e.target.value)} fullWidth={false} size="sm" className="w-full sm:w-auto" data-id="element-4234" />
    </div>;
}
const sections = [{
  id: 'general',
  label: 'Algemeen',
  icon: <Settings className="w-4 h-4" data-id="element-4235" />
}, {
  id: 'governance',
  label: 'Governance',
  icon: <Shield className="w-4 h-4" data-id="element-4236" />
}, {
  id: 'notifications',
  label: 'Meldingen',
  icon: <Bell className="w-4 h-4" data-id="element-4237" />
}, {
  id: 'data',
  label: 'Data & Opslag',
  icon: <Database className="w-4 h-4" data-id="element-4238" />
}] as const;
type SectionId = (typeof sections)[number]['id'];
const DEFAULT_ADMIN_SETTINGS = {
  strictMode: false,
  debugLogging: false,
  showConnectionBadges: true,
  language: 'nl',
  autoValidate: true,
  blockOnError: false,
  warnOnWarning: true,
  governanceLevel: 'standard',
  notifyOnError: true,
  notifyOnWarning: false,
  notifyOnNewAccount: true,
  persistSessions: true,
  maxSessionHistory: '1000',
  autoCleanup: false
};
export function SettingsPage() {
  const [activeSection, setActiveSection] = useState<SectionId>('general');
  const [saved, setSaved] = useState(false);
  const [settings, setSettings] = useState({
    ...DEFAULT_ADMIN_SETTINGS
  });
  // Load persisted settings from dataLayer on mount
  useEffect(() => {
    dataLayer.getAdminSettings().then(persisted => {
      if (persisted) {
        setSettings(prev => ({
          ...prev,
          ...(persisted as Partial<typeof DEFAULT_ADMIN_SETTINGS>)
        }));
      }
    });
  }, []);
  const update = (key: keyof typeof settings, value: boolean | string) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };
  const handleSave = () => {
    dataLayer.saveAdminSettings(settings as Record<string, unknown>);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };
  return <PageLayout title="Instellingen" subtitle="Systeem- en governance-instellingen" breadcrumbs={[{
    label: 'Systeem'
  }, {
    label: 'Instellingen'
  }]} actions={<div className="flex items-center gap-2" data-id="element-4240">
          <ConnectionBadge status="live" data-id="element-4241" />
          <Button onClick={handleSave} variant={saved ? 'success' : 'primary'} size="sm" className={saved ? '' : 'bg-blue-600 hover:bg-blue-700 shadow-none'} data-id="element-4242">
            {saved ? <>
                <Check className="w-4 h-4 mr-1.5" data-id="element-4243" /> Opgeslagen
              </> : 'Opslaan'}
          </Button>
        </div>} data-id="element-4239">
      <div className="flex flex-col md:flex-row gap-6" data-id="element-4244">
        {/* Section Nav */}
        <div className="w-full md:w-56 flex-shrink-0" data-id="element-4245">
          {/* Mobile: Horizontal scrollable tabs */}
          <div className="md:hidden" data-id="element-4246">
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1 overflow-x-auto hide-scrollbar" data-id="element-4247">
              {sections.map(section => <button key={section.id} onClick={() => setActiveSection(section.id)} className={`flex-shrink-0 flex items-center gap-2 px-3.5 py-2 rounded-md text-sm transition-colors whitespace-nowrap focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none ${activeSection === section.id ? 'bg-white text-gray-900 font-medium shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-4248">
                  {section.icon}
                  {section.label}
                </button>)}
            </div>
          </div>

          {/* Desktop: Vertical nav */}
          <nav className="hidden md:flex flex-col gap-1" data-id="element-4249">
            {sections.map(section => <button key={section.id} onClick={() => setActiveSection(section.id)} className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-colors font-medium focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none ${activeSection === section.id ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`} data-id="element-4250">
                {section.icon}
                {section.label}
              </button>)}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0" data-id="element-4251">
          {activeSection === 'general' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-4252">
              <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50" data-id="element-4253">
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-4254">
                  Algemene instellingen
                </h2>
              </div>
              <div className="px-4 sm:px-5" data-id="element-4255">
                <SettingToggle label="Strict mode" description="Striktere validatie en foutafhandeling" value={settings.strictMode} onChange={v => update('strictMode', v)} data-id="element-4256" />
                <SettingToggle label="Debug logging" description="Uitgebreide logging in de browser console" value={settings.debugLogging} onChange={v => update('debugLogging', v)} data-id="element-4257" />
                <SettingToggle label="Verbindingsbadges tonen" description="Toon live/mock badges op elke pagina" value={settings.showConnectionBadges} onChange={v => update('showConnectionBadges', v)} data-id="element-4258" />
                <SettingSelect label="Taal" description="Taal van de admin interface" value={settings.language} options={[{
              value: 'nl',
              label: 'Nederlands'
            }, {
              value: 'en',
              label: 'English'
            }]} onChange={v => update('language', v)} data-id="element-4259" />
              </div>
            </div>}

          {activeSection === 'governance' && <div className="space-y-4" data-id="element-4260">
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-4261">
                <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50" data-id="element-4262">
                  <h2 className="text-sm font-semibold text-gray-900" data-id="element-4263">
                    Governance pipeline
                  </h2>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-4264">
                    Instellingen voor de validatie- en governance-pipeline
                  </p>
                </div>
                <div className="px-4 sm:px-5" data-id="element-4265">
                  <SettingToggle label="Automatisch valideren" description="Valideer vragen automatisch bij het laden" value={settings.autoValidate} onChange={v => update('autoValidate', v)} data-id="element-4266" />
                  <SettingToggle label="Blokkeren bij fouten" description="Blokkeer het systeem bij governance-fouten" value={settings.blockOnError} onChange={v => update('blockOnError', v)} data-id="element-4267" />
                  <SettingToggle label="Waarschuwen bij warnings" description="Toon waarschuwingen bij governance-issues" value={settings.warnOnWarning} onChange={v => update('warnOnWarning', v)} data-id="element-4268" />
                  <SettingSelect label="Governance niveau" description="Strengheid van de governance-regels" value={settings.governanceLevel} options={[{
                value: 'lenient',
                label: 'Licht (alleen fouten)'
              }, {
                value: 'standard',
                label: 'Standaard (fouten + warnings)'
              }, {
                value: 'strict',
                label: 'Strikt (alles)'
              }]} onChange={v => update('governanceLevel', v)} data-id="element-4269" />
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 sm:px-5 py-4 flex items-start gap-3" data-id="element-4270">
                <Info className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" data-id="element-4271" />
                <div data-id="element-4272">
                  <p className="text-sm font-bold text-blue-900" data-id="element-4273">
                    Governance pipeline
                  </p>
                  <p className="text-xs sm:text-sm text-blue-800 mt-1 leading-relaxed" data-id="element-4274">
                    De governance pipeline controleert alle vragen op
                    schema-validatie, type-regels, inhoudskwaliteit en visuele
                    governance. Zie de <strong data-id="element-4275">Validatieregels</strong> pagina
                    voor een volledige compliance-check.
                  </p>
                </div>
              </div>
            </div>}

          {activeSection === 'notifications' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-4276">
              <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50" data-id="element-4277">
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-4278">
                  Meldingsinstellingen
                </h2>
              </div>
              <div className="px-4 sm:px-5" data-id="element-4279">
                <SettingToggle label="Melden bij fouten" description="Stuur een melding bij governance-fouten" value={settings.notifyOnError} onChange={v => update('notifyOnError', v)} data-id="element-4280" />
                <SettingToggle label="Melden bij waarschuwingen" description="Stuur een melding bij governance-waarschuwingen" value={settings.notifyOnWarning} onChange={v => update('notifyOnWarning', v)} data-id="element-4281" />
                <SettingToggle label="Melden bij nieuw account" description="Stuur een melding bij een nieuw account" value={settings.notifyOnNewAccount} onChange={v => update('notifyOnNewAccount', v)} data-id="element-4282" />
              </div>
            </div>}

          {activeSection === 'data' && <div className="space-y-4" data-id="element-4283">
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-4284">
                <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50" data-id="element-4285">
                  <h2 className="text-sm font-semibold text-gray-900" data-id="element-4286">
                    Data & opslag
                  </h2>
                </div>
                <div className="px-4 sm:px-5" data-id="element-4287">
                  <SettingToggle label="Sessies bewaren" description="Bewaar oefensessies in lokale opslag" value={settings.persistSessions} onChange={v => update('persistSessions', v)} data-id="element-4288" />
                  <SettingToggle label="Automatisch opruimen" description="Verwijder oude sessies automatisch" value={settings.autoCleanup} onChange={v => update('autoCleanup', v)} data-id="element-4289" />
                  <SettingSelect label="Max. sessiegeschiedenis" description="Maximum aantal sessies om te bewaren" value={settings.maxSessionHistory} options={[{
                value: '100',
                label: '100 sessies'
              }, {
                value: '500',
                label: '500 sessies'
              }, {
                value: '1000',
                label: '1.000 sessies'
              }, {
                value: '5000',
                label: '5.000 sessies'
              }]} onChange={v => update('maxSessionHistory', v)} data-id="element-4290" />
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 sm:px-5 py-4 flex items-start gap-3" data-id="element-4291">
                <Info className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" data-id="element-4292" />
                <div data-id="element-4293">
                  <p className="text-sm font-bold text-amber-900" data-id="element-4294">
                    Lokale opslag
                  </p>
                  <p className="text-xs sm:text-sm text-amber-800 mt-1 leading-relaxed" data-id="element-4295">
                    Alle data wordt lokaal opgeslagen in de browser. Er is geen
                    externe database verbinding. Data gaat verloren bij het
                    wissen van de browser-cache.
                  </p>
                </div>
              </div>
            </div>}
        </div>
      </div>
    </PageLayout>;
}